import { useApp, useContentStore, useEditorSync, useProblem, useSelectionReference, useUploadJobs, useS3Client } from "@/hooks/contextHooks";
import { AppMeta, Content, NewContentItem, FlashcardMeta, Flashcard } from "@xnav/types/src/common";
import { ReactStateType } from "@xnav/types/src/frontend";
import { EditContent } from "@/pages/editor";
import { useEffect, useState } from "react";
import Loading from "./loading";
import { createClientContent, createServerContent } from "@/functions/editorFunctions";
import { apiGetRequest, apiPutRequest, apiPostRequest, apiDeleteRequest } from "@/functions/requests";
import { v4 } from "uuid";
import { Box, Button, Flex, FormControl, FormLabel, Heading, IconButton, Input, Radio, RadioGroup, Stack, Text, Textarea, VStack, Card, CardBody, CardHeader, useColorModeValue, Modal, ModalOverlay, ModalContent, ModalBody, ModalCloseButton, useDisclosure, Image, SimpleGrid, AlertDialog, AlertDialogBody, AlertDialogFooter, AlertDialogHeader, AlertDialogContent, AlertDialogOverlay, useToast } from "@chakra-ui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faTrash, faListCheck, faCheck, faSave, faUpload, faCrop, faPen, faGamepad, faRocket } from "@fortawesome/free-solid-svg-icons";
import { GetObjectCommand, S3Client } from "@aws-sdk/client-s3";
import { getSignedUrl } from "@aws-sdk/s3-request-presigner";
import { useAuthenticator } from "@aws-amplify/ui-react";
import React, { Suspense, lazy, useRef } from "react";
import Papa from 'papaparse';

import ImageAnnotator from "./ImageAnnotator";
import SignedImage from "./signedImage";

export default function FlashcardEditor() {
    const app = useApp() as AppMeta
    const [, setProblem] = useProblem()
    const [content, setContent] = useState<EditContent>()

    const [selectionReference,] = useSelectionReference() as ReactStateType<NewContentItem | undefined>
    const [pushJob, popJob] = useUploadJobs()

    const [contentStore, setContentStore] = useContentStore() as ReactStateType<Record<string, EditContent>>
    const [editorSync,] = useEditorSync()
    const s3 = useS3Client() as S3Client
    const { user } = useAuthenticator()

    const { isOpen: isAnnotateOpen, onOpen: onAnnotateOpen, onClose: onAnnotateClose } = useDisclosure()
    const [annotateTarget, setAnnotateTarget] = useState<{ qIndex: number, imgIndex: number, url: string } | null>(null)
    const [activeQuestIndex, setActiveQuestIndex] = useState<number>(0)

    const [isDeployAlertOpen, setIsDeployAlertOpen] = useState(false)
    const [isDeploying, setIsDeploying] = useState(false)
    const [isDeleteAlertOpen, setIsDeleteAlertOpen] = useState(false)
    const [isDeleting, setIsDeleting] = useState(false)
    const [questIndexToDelete, setQuestIndexToDelete] = useState<number | null>(null)
    const cancelDeployRef = useRef<HTMLButtonElement>(null)
    const cancelDeleteRef = useRef<HTMLButtonElement>(null)
    const toast = useToast()

    const handleDeployQuest = async () => {
        if (!content?.flashcardData?.quests || activeQuestIndex < 0) return;
        const quest = content.flashcardData.quests[activeQuestIndex];
        setIsDeploying(true);
        try {
            // First make sure the content is saved to edit-bucket/DynamoDB
            await pushContent();
            
            // Now push the specific quest to the live DynamoDB table
            await apiPostRequest(`/flashcards/deploy/${app.id}`, quest);
            
            toast({
                title: "Quest Published Live!",
                description: `"${quest.title}" is now available to mobile users.`,
                status: "success",
                duration: 5000,
                isClosable: true,
                position: "top-right"
            });
            setIsDeployAlertOpen(false);
        } catch (error: any) {
            console.error("Failed to deploy quest", error);
            const errMsg = error.response?.data?.message || error.message || "Unknown error";
            toast({
                title: "Deployment Failed",
                description: `Error: ${errMsg}`,
                status: "error",
                duration: 8000,
                isClosable: true,
                position: "top-right"
            });
        } finally {
            setIsDeploying(false);
        }
    };

    async function fetchContent() {
        if (!selectionReference || selectionReference.type !== 'flashcard') return
        const temp: Content = {
            id: selectionReference.id,
            title: selectionReference.title,
            hotSpots: [],
            polygons: [],
            wisdomDocTitles: [],
            flashcardData: { quests: [] }
        }

        let loaded: Content = temp

        try {
            // S3 Bypass Fix for Flashcard
            const command = new GetObjectCommand({
                Bucket: import.meta.env.VITE_EDIT_BUCKET_NAME,
                Key: `${app.id}/contents/content/${selectionReference.id}.json`
            })
            const url = await getSignedUrl(s3 as any, command as any, { expiresIn: 360 })
            const response = await fetch(url)
            if (response.ok) {
                const strData = await response.text()
                let parsed = JSON.parse(strData)
                if (!parsed.flashcardData) {
                    parsed.flashcardData = { quests: [] }
                }
                if (!parsed.flashcardData.quests && parsed.flashcardData.flashcards) {
                    parsed.flashcardData.quests = [{ id: v4(), title: "Quest 1", flashcards: parsed.flashcardData.flashcards }]
                }
                loaded = parsed
            } else {
                throw new Error("S3 fetch failed with status: " + response.status)
            }
        } catch (e) {
            console.log("S3 direct fetch failed for flashcard, trying API", e)
            try {
                let result = await apiGetRequest<string, Content | string>(`/content/${app.id}/${selectionReference.id}`).catch(() => undefined)

                if (result && result !== '') {
                    if (typeof result === 'string') {
                        try {
                            result = JSON.parse(result)
                        } catch (e) {
                            console.error("Failed to parse flashcard content:", e)
                        }
                    }
                    if (result && typeof result === 'object') {
                        const loadedContent = result as Content;
                        if (!loadedContent.flashcardData) {
                            loadedContent.flashcardData = { quests: [] }
                        }
                        if (!loadedContent.flashcardData.quests && loadedContent.flashcardData.flashcards) {
                            loadedContent.flashcardData.quests = [{ id: v4(), title: "Quest 1", flashcards: loadedContent.flashcardData.flashcards }]
                        }
                        loaded = loadedContent
                    }
                }
            } catch (error) {
                console.error("Error fetching flashcard:", error)
            }
        }

        const clientContent = createClientContent(loaded)
        setContent(clientContent)
        setContentStore(prev => ({ ...prev, [clientContent.id]: clientContent }))
    }

    async function pushContent() {
        if (!content) return
        pushJob()
        try {
            await apiPutRequest<Content, undefined>(`/content/${app.id}/${content.id}`, createServerContent(content))
        } catch (error: any) {
            console.error(error);
            if (error.response && error.response.data) {
                alert("Server rejected save: " + JSON.stringify(error.response.data));
            }
            setProblem(error)
        }
        popJob()
    }

    useEffect(() => {
        if (!selectionReference || selectionReference.type !== 'flashcard') {
            setContent(undefined)
            return
        }
        if (contentStore[selectionReference.id]) {
            // Ensure stored content has flashcard structure
            const stored = contentStore[selectionReference.id]
            if (!stored.flashcardData) {
                stored.flashcardData = { quests: [] }
            }
            if (!stored.flashcardData.quests && stored.flashcardData.flashcards) {
                stored.flashcardData.quests = [{ id: v4(), title: "Quest 1", flashcards: stored.flashcardData.flashcards }]
            }
            setContent(stored)
            return
        }
        fetchContent()
    }, [selectionReference?.id])

    useEffect(() => {
        if (content) {
            setContentStore(prev => {
                if (prev[content.id] === content) return prev;
                return { ...prev, [content.id]: content };
            });
        }
    }, [content, setContentStore])

    useEffect(() => {
        if (editorSync) pushContent()
    }, [editorSync])

    // Update Helpers
    
    const updateQuests = (newQuests: any[]) => {
        if (!content) return;
        setContent({
            ...content,
            flashcardData: {
                ...content.flashcardData,
                quests: newQuests
            }
        } as EditContent);
    };

    const addQuest = () => {
        if (!content?.flashcardData) return;
        const newQuests = [...(content.flashcardData.quests || [])];
        newQuests.push({ id: v4(), title: `Quest ${newQuests.length + 1}`, flashcards: [] });
        updateQuests(newQuests);
        setActiveQuestIndex(newQuests.length - 1);
    };

    const updateQuestTitle = (index: number, title: string) => {
        if (!content?.flashcardData?.quests) return;
        const newQuests = [...content.flashcardData.quests];
        newQuests[index] = { ...newQuests[index], title };
        updateQuests(newQuests);
    };

    const deleteQuest = (index: number) => {
        setQuestIndexToDelete(index);
        setIsDeleteAlertOpen(true);
    };

    const confirmDeleteQuest = async () => {
        if (questIndexToDelete === null || !content?.flashcardData?.quests) return;
        const questToDelete = content.flashcardData.quests[questIndexToDelete];
        setIsDeleting(true);
        try {
            // 1. Delete from live DynamoDB
            if (questToDelete && questToDelete.id) {
                await apiDeleteRequest(`/flashcards/delete/${app.id}/${questToDelete.id}`);
            }
            
            // 2. Delete from content JSON in S3 edit-bucket
            const newQuests = content.flashcardData.quests.filter((_, i) => i !== questIndexToDelete);
            const updatedContent = {
                ...content,
                flashcardData: {
                    ...content.flashcardData,
                    quests: newQuests
                }
            };
            pushJob();
            await apiPutRequest(`/content/${app.id}/${content.id}`, createServerContent(updatedContent));
            popJob();
            
            // 3. Update React state
            updateQuests(newQuests);
            setActiveQuestIndex(0);
            
            toast({
                title: "Quest Deleted",
                description: `"${questToDelete.title}" has been deleted successfully.`,
                status: "success",
                duration: 5000,
                isClosable: true,
                position: "top-right"
            });
        } catch (error: any) {
            console.error("Failed to delete quest", error);
            const errMsg = error.response?.data?.message || error.message || "Unknown error";
            
            toast({
                title: "Failed to Delete Quest",
                description: `Error: ${errMsg}`,
                status: "error",
                duration: 8000,
                isClosable: true,
                position: "top-right"
            });
        } finally {
            setIsDeleting(false);
            setIsDeleteAlertOpen(false);
            setQuestIndexToDelete(null);
        }
    };

    const updateFlashcards = (newFlashcards: Flashcard[]) => {
        if (!content?.flashcardData?.quests) return;
        const newQuests = [...content.flashcardData.quests];
        if (!newQuests[activeQuestIndex]) return;
        newQuests[activeQuestIndex] = {
            ...newQuests[activeQuestIndex],
            flashcards: newFlashcards
        };
        updateQuests(newQuests);
    };


    const addFlashcard = () => {
        if (!content?.flashcardData) return;
        const newQ: Flashcard = {
            id: v4(),
            text: "New Flashcard",
            options: ["Option A", "Option B", "Option C", "Option D"],
            correctAnswer: 0,
            explanation: "",
            images: []
        };
        updateFlashcards([...(content.flashcardData.quests?.[activeQuestIndex]?.flashcards || []), newQ]);
    };

    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleCsvUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file || !content?.flashcardData) return;

        Papa.parse(file, {
            header: false,
            skipEmptyLines: true,
            complete: (results) => {
                const rows = results.data as string[][];
                // Skip header row if it contains "Flashcard" or "Option"
                const dataRows = rows.filter(row => row.length > 1);
                let startIndex = 0;
                if (dataRows.length > 0 && 
                    (dataRows[0][1]?.toLowerCase().includes("flashcard") || dataRows[0][2]?.toLowerCase().includes("option"))) {
                    startIndex = 1;
                }

                const newFlashcards: Flashcard[] = [];
                for (let i = startIndex; i < dataRows.length; i++) {
                    const row = dataRows[i];
                    if (row.length < 8) continue; // Basic validation

                    const flashcardText = row[1] || "";
                    if (!flashcardText.trim()) continue;

                    const optionA = row[2] || "";
                    const optionB = row[3] || "";
                    const optionC = row[4] || "";
                    const optionD = row[5] || "";
                    
                    const correctLetter = (row[6] || "").trim().toUpperCase();
                    let correctAnswerIndex = 0;
                    if (correctLetter === 'B') correctAnswerIndex = 1;
                    else if (correctLetter === 'C') correctAnswerIndex = 2;
                    else if (correctLetter === 'D') correctAnswerIndex = 3;

                    const explanation = row[7] || "";

                    const newQ: Flashcard = {
                        id: v4(),
                        text: flashcardText,
                        options: [optionA, optionB, optionC, optionD],
                        correctAnswer: correctAnswerIndex,
                        explanation: explanation,
                        images: []
                    };
                    newFlashcards.push(newQ);
                }

                if (newFlashcards.length > 0) {
                    updateFlashcards([...(content.flashcardData.quests?.[activeQuestIndex]?.flashcards || []), ...newFlashcards]);
                }
                
                // Reset file input
                if (fileInputRef.current) {
                    fileInputRef.current.value = "";
                }
            },
            error: (error) => {
                console.error("Error parsing CSV:", error);
            }
        });
    };

    const downloadTemplate = () => {
        const template = "Flashcard Number,Flashcard Text,Option A,Option B,Option C,Option D,Correct Option (A/B/C/D),Explanation\n1,What is the powerhouse of the cell?,Nucleus,Mitochondria,Ribosome,Endoplasmic Reticulum,B,Mitochondria generate ATP which powers the cell.\n2,Which planet is known as the Red Planet?,Earth,Venus,Jupiter,Mars,D,Mars appears red due to iron oxide.";
        const blob = new Blob([template], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'flashcard_import_template.csv';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    const updateFlashcard = (index: number, field: keyof Flashcard, value: any) => {
        if (!content?.flashcardData) return;
        const updated = [...(content.flashcardData.quests?.[activeQuestIndex]?.flashcards || [])];
        updated[index] = { ...updated[index], [field]: value };
        updateFlashcards(updated);
    };

    const updateOption = (qIndex: number, optIndex: number, value: string) => {
        if (!content?.flashcardData) return;
        const updated = [...(content.flashcardData.quests?.[activeQuestIndex]?.flashcards || [])];
        const newOptions = [...updated[qIndex].options];
        newOptions[optIndex] = value;
        updated[qIndex] = { ...updated[qIndex], options: newOptions };
        updateFlashcards(updated);
    }

    const handleImageUpload = async (qIndex: number, e: React.ChangeEvent<HTMLInputElement>) => {
        if (!content?.flashcardData || !e.target.files) return;
        const files = Array.from(e.target.files);
        
        const uploadToastId = toast({
            title: "Uploading image(s)...",
            status: "info",
            duration: null,
            isClosable: false,
            position: "top-right"
        });

        try {
            const newImages = await Promise.all(files.map(file => new Promise<any>(async (resolve, reject) => {
                const reader = new FileReader();
                reader.onloadend = async () => {
                    try {
                        const base64Data = (reader.result as string).split(',')[1];
                        const response = await apiPostRequest<any, { imageUrl: string, fileKey: string }>('/upload-image', {
                            fileName: file.name,
                            fileContent: base64Data
                        });
                        resolve({ url: response.imageUrl, tag: '', originalUrl: response.imageUrl });
                    } catch (err) {
                        reject(err);
                    }
                };
                reader.onerror = reject;
                reader.readAsDataURL(file);
            })));

            const updated = [...(content.flashcardData.quests?.[activeQuestIndex]?.flashcards || [])];
            updated[qIndex] = { ...updated[qIndex], images: [...(updated[qIndex].images || []), ...newImages] };
            updateFlashcards(updated);
            
            toast.update(uploadToastId, {
                title: "Upload complete!",
                status: "success",
                duration: 3000,
                isClosable: true
            });
        } catch (error) {
            console.error("Failed to upload image(s):", error);
            toast.update(uploadToastId, {
                title: "Upload failed",
                description: "Failed to upload one or more images.",
                status: "error",
                duration: 5000,
                isClosable: true
            });
        }
    };



    const removeImage = (qIndex: number, imgIndex: number) => {
        if (!content?.flashcardData) return;
        const updated = [...(content.flashcardData.quests?.[activeQuestIndex]?.flashcards || [])];
        const newImages = [...(updated[qIndex].images || [])];
        newImages.splice(imgIndex, 1);
        updated[qIndex] = { ...updated[qIndex], images: newImages };
        updateFlashcards(updated);
    };

    const handleOpenEditor = async (qIndex: number, imgIndex: number, url: string) => {
        let finalUrl = url;
        if (url.startsWith('http')) {
            try {
                let signedUrlStr = url;
                const match = url.match(/^https:\/\/([^.]+)\.s3\.([^.]+)\.amazonaws\.com\/(.+)$/);
                if (match && s3) {
                    const bucket = match[1];
                    const key = decodeURIComponent(match[3]);
                    const command = new GetObjectCommand({
                        Bucket: bucket,
                        Key: key
                    });
                    signedUrlStr = await getSignedUrl(s3 as any, command as any, { expiresIn: 3600 });
                }

                // Fetch and convert to base64 to avoid Canvas Tainting / CORS issues in markerjs2
                const res = await fetch(signedUrlStr);
                const blob = await res.blob();
                finalUrl = await new Promise<string>((resolve) => {
                    const reader = new FileReader();
                    reader.onloadend = () => resolve(reader.result as string);
                    reader.readAsDataURL(blob);
                });
            } catch (e) {
                console.error("Failed to load image as blob for editing, using original URL", e);
            }
        }
        setAnnotateTarget({ qIndex, imgIndex, url: finalUrl });
        onAnnotateOpen();
    };

    const handleCloseAnnotator = () => {
        setAnnotateTarget(null);
        onAnnotateClose();
    };




    const deleteFlashcard = (index: number) => {
        if (!content?.flashcardData) return;
        const updated = (content.flashcardData.quests?.[activeQuestIndex]?.flashcards || []).filter((_, i) => i !== index);
        updateFlashcards(updated);
    };

    if (!selectionReference || selectionReference.type !== 'flashcard') return <></>
    if (!content) return <Loading message="Fetching flashcard..." />
    if (!content.flashcardData) return <Loading message="Initializing flashcard..." />

    return (
        <Flex direction="column" w="100%" h="100%" p={8} overflowY="auto" bg={useColorModeValue("gray.50", "gray.900")} css={{ '&::-webkit-scrollbar': { display: 'none' } }}>
            <Box maxW="1000px" w="100%" mx="auto">
                <Flex justify="space-between" align="center" mb={8}>
                    <Box>
                        <Heading 
                            size="2xl" 
                            bgGradient="linear(to-r, purple.400, blue.500)" 
                            bgClip="text"
                            fontWeight="extrabold"
                            letterSpacing="tight"
                        >
                            Flashcard Engine
                        </Heading>
                        <Text color="gray.500" mt={2} fontSize="lg">Craft engaging assessments for {content.title}</Text>
                    </Box>
                    <Flex gap={4} align="center">
                        <Button 
                            variant="outline" 
                            colorScheme="purple" 
                            size="sm" 
                            onClick={downloadTemplate}
                            borderRadius="xl"
                        >
                            Download CSV Template
                        </Button>
                        <Box bg="white" _dark={{ bg: "whiteAlpha.100" }} p={3} borderRadius="2xl" shadow="sm">
                            <FontAwesomeIcon icon={faListCheck} size="2x" color="#9F7AEA" />
                        </Box>
                    </Flex>
                </Flex>

                <Flex gap={4} mb={8} overflowX="auto" pb={2} css={{ '&::-webkit-scrollbar': { display: 'none' } }}>
                    {content.flashcardData.quests?.map((q, idx) => (
                        <Flex 
                            key={q.id}
                            minW="180px"
                            bg={activeQuestIndex === idx ? useColorModeValue("purple.50", "purple.900") : useColorModeValue("white", "gray.800")}
                            border="2px solid"
                            borderColor={activeQuestIndex === idx ? "purple.400" : useColorModeValue("gray.200", "gray.700")}
                            borderRadius="2xl"
                            p={4}
                            shadow={activeQuestIndex === idx ? "md" : "sm"}
                            cursor="pointer"
                            onClick={() => setActiveQuestIndex(idx)}
                            direction="column"
                            gap={2}
                            position="relative"
                        >
                            {activeQuestIndex === idx && (
                                <IconButton
                                    aria-label="Delete Quest"
                                    icon={<FontAwesomeIcon icon={faTrash} />}
                                    size="xs"
                                    position="absolute"
                                    top={2}
                                    right={2}
                                    colorScheme="red"
                                    variant="ghost"
                                    onClick={(e) => { e.stopPropagation(); deleteQuest(idx); }}
                                />
                            )}
                            <Text fontSize="xs" fontWeight="bold" color="purple.500" textTransform="uppercase">Quest {idx + 1}</Text>
                            <Input 
                                value={q.title} 
                                onChange={(e) => updateQuestTitle(idx, e.target.value)} 
                                variant="flushed" 
                                size="sm" 
                                fontWeight="bold" 
                                placeholder="Quest Title" 
                                onClick={(e) => e.stopPropagation()}
                            />
                            <Text fontSize="xs" color="gray.500">{q.flashcards?.length || 0} cards</Text>
                        </Flex>
                    ))}
                    <Flex 
                        minW="180px"
                        bg="transparent"
                        border="2px dashed"
                        borderColor={useColorModeValue("gray.300", "gray.600")}
                        borderRadius="2xl"
                        p={4}
                        align="center"
                        justify="center"
                        cursor="pointer"
                        _hover={{ bg: useColorModeValue("gray.50", "gray.700") }}
                        onClick={addQuest}
                    >
                        <VStack spacing={2}>
                            <FontAwesomeIcon icon={faPlus} color="gray" />
                            <Text fontWeight="bold" color="gray.500">Add Quest</Text>
                        </VStack>
                    </Flex>
                </Flex>

                {content.flashcardData.quests && content.flashcardData.quests.length > 0 && activeQuestIndex >= 0 && activeQuestIndex < content.flashcardData.quests.length ? (
                <VStack spacing={6} align="stretch">
                    <Flex justify="space-between" align="center" bg={useColorModeValue("white", "gray.800")} p={5} borderRadius="3xl" shadow="sm" border="1px solid" borderColor={useColorModeValue("purple.200", "purple.700")}>
                        <Box>
                            <Heading size="md" color="purple.500">{content.flashcardData.quests[activeQuestIndex].title}</Heading>
                            <Text color="gray.500" fontSize="sm">Manage flashcards for this quest.</Text>
                        </Box>
                        <Button colorScheme="purple" rightIcon={<FontAwesomeIcon icon={faRocket} />} onClick={() => setIsDeployAlertOpen(true)}>
                            Publish Quest Live
                        </Button>
                    </Flex>
                    {(content.flashcardData.quests?.[activeQuestIndex]?.flashcards || []).map((q, qIndex) => (
                        <Box 
                            key={q.id} 
                            bg={useColorModeValue("rgba(255, 255, 255, 0.8)", "rgba(26, 32, 44, 0.8)")}
                            backdropFilter="blur(16px)"
                            border="1px solid"
                            borderColor={useColorModeValue("whiteAlpha.600", "whiteAlpha.200")}
                            borderRadius="3xl"
                            shadow="xl"
                            p={8}
                            transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
                            _hover={{ transform: 'translateY(-4px)', shadow: '2xl' }}
                        >
                            <Flex justify="space-between" align="center" mb={6}>
                                <Flex align="center" gap={3}>
                                    <Flex 
                                        bgGradient="linear(to-br, purple.400, blue.500)" 
                                        color="white" 
                                        w={10} 
                                        h={10} 
                                        borderRadius="xl" 
                                        align="center" 
                                        justify="center" 
                                        fontWeight="bold"
                                        shadow="md"
                                    >
                                        {qIndex + 1}
                                    </Flex>
                                    <Heading size="md" color={useColorModeValue("gray.700", "white")}>Flashcard Details</Heading>
                                </Flex>
                                <IconButton
                                    icon={<FontAwesomeIcon icon={faTrash} />}
                                    aria-label="Delete Flashcard"
                                    colorScheme="red"
                                    variant="ghost"
                                    borderRadius="xl"
                                    _hover={{ bg: "red.50", color: "red.600" }}
                                    onClick={() => deleteFlashcard(qIndex)}
                                />
                            </Flex>

                            <VStack spacing={6} align="stretch">
                                <FormControl>
                                    <FormLabel fontSize="sm" fontWeight="bold" color="gray.500" textTransform="uppercase" letterSpacing="wider">Flashcard Text</FormLabel>
                                        <Textarea
                                            value={q.text}
                                            onChange={(e) => updateFlashcard(qIndex, 'text', e.target.value)}
                                            placeholder="What is the main concept of..."
                                            rows={2}
                                            size="lg"
                                            borderRadius="xl"
                                            border="2px solid"
                                            borderColor={useColorModeValue("gray.100", "gray.700")}
                                            bg={useColorModeValue("gray.50", "gray.800")}
                                            _focus={{ borderColor: "purple.400", bg: useColorModeValue("white", "gray.900"), shadow: "outline" }}
                                            transition="all 0.2s"
                                            mb={3}
                                        />
                                        <Flex direction="column" gap={4}>
                                            <FormControl>
                                                <FormLabel fontSize="xs" color="gray.400">Flashcard Images</FormLabel>
                                                <Button 
                                                    as="label" 
                                                    size="sm" 
                                                    leftIcon={<FontAwesomeIcon icon={faUpload} />} 
                                                    cursor="pointer"
                                                    colorScheme="blue"
                                                    variant="outline"
                                                >
                                                    Upload Images
                                                    <input 
                                                        type="file" 
                                                        hidden 
                                                        multiple 
                                                        accept="image/*" 
                                                        onChange={(e) => handleImageUpload(qIndex, e)} 
                                                    />
                                                </Button>

                                                {q.images && q.images.length > 0 && (
                                                    <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4} mt={4}>
                                                        {q.images.map((img, imgIndex) => (
                                                            <Box key={imgIndex} position="relative" borderRadius="md" overflow="hidden" border="1px solid" borderColor="gray.200" bg="white" p={2}>
                                                                <Flex justify="space-between" mb={2}>
                                                                    <Flex gap={2}>
                                                                        <IconButton 
                                                                            size="xs" 
                                                                            aria-label="Draw Annotations" 
                                                                            icon={<FontAwesomeIcon icon={faPen} />} 
                                                                            colorScheme="purple" 
                                                                            onClick={() => handleOpenEditor(qIndex, imgIndex, img.originalUrl || img.url)} 
                                                                        />
                                                                    </Flex>
                                                                    <IconButton 
                                                                        size="xs" 
                                                                        aria-label="Delete" 
                                                                        icon={<FontAwesomeIcon icon={faTrash} />} 
                                                                        colorScheme="red" 
                                                                        variant="ghost" 
                                                                        onClick={() => removeImage(qIndex, imgIndex)} 
                                                                    />
                                                                </Flex>
                                                                <SignedImage s3Client={s3} src={img.url} alt="Flashcard media" objectFit="contain" maxH="120px" w="100%" borderRadius="sm" />
                                                            </Box>
                                                        ))}
                                                    </SimpleGrid>
                                                )}
                                            </FormControl>
                                        </Flex>
                                    </FormControl>

                                <FormControl>
                                    <FormLabel fontSize="sm" fontWeight="bold" color="gray.500" textTransform="uppercase" letterSpacing="wider">Answer Options</FormLabel>
                                    <Flex direction="column" gap={3}>
                                        {q.options.map((opt, optIndex) => {
                                            const isCorrect = q.correctAnswer === optIndex;
                                            return (
                                                <Flex 
                                                    key={optIndex} 
                                                    align="center" 
                                                    gap={3} 
                                                    p={2}
                                                    borderRadius="xl"
                                                    bg={isCorrect ? useColorModeValue("green.50", "rgba(72, 187, 120, 0.15)") : "transparent"}
                                                    border="2px solid"
                                                    borderColor={isCorrect ? "green.400" : "transparent"}
                                                    transition="all 0.2s"
                                                >
                                                    <IconButton
                                                        aria-label="Mark Correct"
                                                        icon={<FontAwesomeIcon icon={faCheck} />}
                                                        colorScheme={isCorrect ? "green" : "gray"}
                                                        variant={isCorrect ? "solid" : "outline"}
                                                        borderRadius="full"
                                                        onClick={() => updateFlashcard(qIndex, 'correctAnswer', optIndex)}
                                                        size="sm"
                                                        shadow={isCorrect ? "md" : "none"}
                                                    />
                                                    <Flex direction="column" w="100%" gap={2}>
                                                        <Input
                                                            size="lg"
                                                            value={opt}
                                                            onChange={(e) => updateOption(qIndex, optIndex, e.target.value)}
                                                            borderRadius="lg"
                                                            border="none"
                                                            bg={useColorModeValue("gray.100", "gray.800")}
                                                            _focus={{ bg: useColorModeValue("white", "gray.700"), shadow: "sm" }}
                                                            placeholder={`Option ${String.fromCharCode(65 + optIndex)}`}
                                                            fontWeight={isCorrect ? "bold" : "normal"}
                                                            color={isCorrect ? useColorModeValue("green.700", "green.200") : "inherit"}
                                                        />
                                                    </Flex>
                                                </Flex>
                                            );
                                        })}
                                    </Flex>
                                </FormControl>

                                <FormControl>
                                    <FormLabel fontSize="sm" fontWeight="bold" color="gray.500" textTransform="uppercase" letterSpacing="wider">Global Explanation</FormLabel>
                                    <Textarea
                                        value={q.explanation}
                                        onChange={(e) => updateFlashcard(qIndex, 'explanation', e.target.value)}
                                        placeholder="Explain why the correct answer is right..."
                                        rows={2}
                                        borderRadius="xl"
                                        border="2px solid"
                                        borderColor={useColorModeValue("gray.100", "gray.700")}
                                        bg={useColorModeValue("gray.50", "gray.800")}
                                        _focus={{ borderColor: "purple.400", bg: useColorModeValue("white", "gray.900") }}
                                        transition="all 0.2s"
                                    />
                                </FormControl>
                            </VStack>
                        </Box>
                    ))}

                    <Flex gap={4} w="100%">
                        <Button
                            leftIcon={<FontAwesomeIcon icon={faPlus} />}
                            onClick={addFlashcard}
                            size="lg"
                            flex={1}
                            height="80px"
                            borderRadius="3xl"
                            border="2px dashed"
                            borderColor={useColorModeValue("purple.300", "purple.600")}
                            bg={useColorModeValue("purple.50", "rgba(159, 122, 234, 0.1)")}
                            color={useColorModeValue("purple.600", "purple.300")}
                            _hover={{ bg: useColorModeValue("purple.100", "rgba(159, 122, 234, 0.2)"), transform: "scale(1.01)" }}
                            _active={{ transform: "scale(0.98)" }}
                            transition="all 0.2s"
                            fontWeight="bold"
                            fontSize="lg"
                        >
                            Add New Flashcard
                        </Button>
                        <Button
                            as="label"
                            leftIcon={<FontAwesomeIcon icon={faUpload} />}
                            size="lg"
                            flex={1}
                            height="80px"
                            borderRadius="3xl"
                            border="2px dashed"
                            borderColor={useColorModeValue("blue.300", "blue.600")}
                            bg={useColorModeValue("blue.50", "rgba(66, 153, 225, 0.1)")}
                            color={useColorModeValue("blue.600", "blue.300")}
                            _hover={{ bg: useColorModeValue("blue.100", "rgba(66, 153, 225, 0.2)"), transform: "scale(1.01)" }}
                            _active={{ transform: "scale(0.98)" }}
                            transition="all 0.2s"
                            fontWeight="bold"
                            fontSize="lg"
                            cursor="pointer"
                        >
                            Import from CSV/Excel
                            <input
                                type="file"
                                hidden
                                accept=".csv"
                                ref={fileInputRef}
                                onChange={handleCsvUpload}
                            />
                        </Button>
                    </Flex>
                </VStack>
                ) : (
                    <Flex justify="center" align="center" h="200px" bg={useColorModeValue("white", "gray.800")} borderRadius="3xl" shadow="sm" border="1px dashed" borderColor={useColorModeValue("gray.300", "gray.600")}>
                        <VStack spacing={4}>
                            <FontAwesomeIcon icon={faGamepad} size="3x" color="gray" opacity={0.3} />
                            <Text color="gray.500" fontWeight="bold">Select or add a Quest above to start adding flashcards.</Text>
                        </VStack>
                    </Flex>
                )}
            </Box>



            <Modal isOpen={isAnnotateOpen} onClose={handleCloseAnnotator} size="full" motionPreset="none" trapFocus={false}>
                <ModalOverlay backdropFilter="blur(10px)" />
                <ModalContent bg="gray.900" color="white" h="100vh" m={0} borderRadius={0}>
                    <ModalCloseButton zIndex={9999} color="white" bg="blackAlpha.500" _hover={{ bg: "blackAlpha.800" }} />
                    <ModalBody p={6} h="100%" position="relative" display="flex" justifyContent="center" alignItems="center">
                        {annotateTarget && (
                            <Box w="100%" maxW="1400px" h="90vh">
                                <ImageAnnotator
                                    imageUrl={annotateTarget.url}
                                    initialState={
                                        ((content?.flashcardData?.quests?.[activeQuestIndex]?.flashcards || [])[annotateTarget.qIndex]?.images?.[annotateTarget.imgIndex] as any)?.markerState
                                    }
                                    onSave={async (annotatedImageUrl, state) => {
                                        handleCloseAnnotator();
                                        const saveToastId = toast({
                                            title: "Saving annotation...",
                                            status: "info",
                                            duration: null,
                                            isClosable: false,
                                            position: "top-right"
                                        });
                                        try {
                                            const base64Data = annotatedImageUrl.split(',')[1];
                                            const fileName = `annotated_${v4()}.png`;
                                            const response = await apiPostRequest<any, { imageUrl: string, fileKey: string }>('/upload-image', {
                                                fileName,
                                                fileContent: base64Data
                                            });
                                            const newFlashcards = [...((content?.flashcardData?.quests?.[activeQuestIndex]?.flashcards || []) || [])];
                                            const q = newFlashcards[annotateTarget.qIndex];
                                            if (q && q.images && q.images[annotateTarget.imgIndex]) {
                                                const img = q.images[annotateTarget.imgIndex] as any;
                                                if (!img.originalUrl) {
                                                    img.originalUrl = img.url;
                                                }
                                                img.url = response.imageUrl;
                                                img.markerState = state;
                                            }
                                            updateFlashcards(newFlashcards);
                                            toast.update(saveToastId, {
                                                title: "Annotation saved!",
                                                status: "success",
                                                duration: 3000,
                                                isClosable: true
                                            });
                                        } catch (error) {
                                            console.error("Failed to upload annotated image:", error);
                                            toast.update(saveToastId, {
                                                title: "Failed to save annotation",
                                                description: "Please try again.",
                                                status: "error",
                                                duration: 5000,
                                                isClosable: true
                                            });
                                        }
                                    }}
                                    onCancel={handleCloseAnnotator}
                                />
                            </Box>
                        )}
                    </ModalBody>
                </ModalContent>
            </Modal>

            <AlertDialog
                isOpen={isDeployAlertOpen}
                leastDestructiveRef={cancelDeployRef}
                onClose={() => setIsDeployAlertOpen(false)}
            >
                <AlertDialogOverlay backdropFilter="blur(4px)">
                    <AlertDialogContent borderRadius="2xl" mt="20vh">
                        <AlertDialogHeader fontSize="lg" fontWeight="bold">
                            Publish Quest to Mobile Devices?
                        </AlertDialogHeader>

                        <AlertDialogBody>
                            Are you sure you want to deploy <strong>{content.flashcardData.quests?.[activeQuestIndex]?.title}</strong>? <br/><br/>
                            Once published, this quest will immediately become available to all users who have downloaded this course on the mobile app. This action is instantaneous.
                        </AlertDialogBody>

                        <AlertDialogFooter>
                            <Button ref={cancelDeployRef} onClick={() => setIsDeployAlertOpen(false)} borderRadius="xl" isDisabled={isDeploying}>
                                Cancel
                            </Button>
                            <Button colorScheme="purple" onClick={handleDeployQuest} ml={3} borderRadius="xl" isLoading={isDeploying} leftIcon={<FontAwesomeIcon icon={faRocket} />}>
                                Publish Now
                            </Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>

            <AlertDialog
                isOpen={isDeleteAlertOpen}
                leastDestructiveRef={cancelDeleteRef}
                onClose={() => setIsDeleteAlertOpen(false)}
            >
                <AlertDialogOverlay backdropFilter="blur(4px)">
                    <AlertDialogContent borderRadius="2xl" mt="20vh">
                        <AlertDialogHeader fontSize="lg" fontWeight="bold">
                            Delete Quest?
                        </AlertDialogHeader>

                        <AlertDialogBody>
                            Are you sure you want to delete <strong>{questIndexToDelete !== null ? content.flashcardData.quests?.[questIndexToDelete]?.title : ""}</strong>?<br/><br/>
                            This will remove the quest from the dashboard. If this quest was published live, it will also be deleted from the mobile app.
                        </AlertDialogBody>

                        <AlertDialogFooter>
                            <Button ref={cancelDeleteRef} onClick={() => setIsDeleteAlertOpen(false)} borderRadius="xl" isDisabled={isDeleting}>
                                Cancel
                            </Button>
                            <Button colorScheme="red" onClick={confirmDeleteQuest} ml={3} borderRadius="xl" isLoading={isDeleting} leftIcon={<FontAwesomeIcon icon={faTrash} />}>
                                Delete
                            </Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>
        </Flex>
    )
}
